import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { InfoIcon } from 'lucide-react';
import { FinanceCircular, Vehicle } from '../../types/catalog';
import { OfferContext, circularToSimulation } from '../../utils/finance';
import { bestCircularQuote, circularToOffer, eligibleLines, managementFeeText, maxFinanceable, quoteCircular, rateTypeLabel } from '../../utils/circular';
import { formatARS, formatDate, formatPct } from '../../utils/format';
import { CircularTable } from './CircularTable';
import { OfferStatus } from '../OfferStatus';
import { Modal } from '../ui/Modal';
import { InquiryForm } from '../InquiryForm';
interface CircularSimulatorProps {
  vehicle: Vehicle;
  circular: FinanceCircular;
}
const STEP = 100000;
export function CircularSimulator({
  vehicle,
  circular
}: CircularSimulatorProps) {
  const lines = useMemo(() => eligibleLines(circular, vehicle), [circular, vehicle]);
  const best = useMemo(() => bestCircularQuote(circular, vehicle), [circular, vehicle]);
  const [lineId, setLineId] = useState(best?.line.id ?? lines[0]?.id ?? '');
  const [term, setTerm] = useState(best?.row.term ?? lines[0]?.terms[0]?.term ?? 0);
  const [financed, setFinanced] = useState(best?.financed ?? 0);
  const [askOpen, setAskOpen] = useState(false);
  const line = lines.find(l => l.id === lineId) ?? lines[0];
  const row = line?.terms.find(t => t.term === term) ?? line?.terms[line.terms.length - 1];
  const cap = line ? maxFinanceable(line, vehicle.price) : 0;

  // Al cambiar de unidad, vuelve a la línea recomendada.
  useEffect(() => {
    if (!best) return;
    setLineId(best.line.id);
    setTerm(best.row.term);
    setFinanced(best.financed);
  }, [vehicle.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectLine = (id: string, t?: number) => {
    const next = lines.find(l => l.id === id);
    if (!next) return;
    setLineId(id);
    const nextTerm = t ?? (next.terms.some(x => x.term === term) ? term : next.terms[next.terms.length - 1].term);
    setTerm(nextTerm);
    setFinanced(maxFinanceable(next, vehicle.price));
  };
  if (!line || !row) {
    return <div className="rounded-2xl border border-dashed border-line p-10 text-center text-sm text-mist" data-id="element-978" data-mp="29ps2373kxsat">Esta unidad no tiene líneas habilitadas en la circular vigente.</div>;
  }
  const q = quoteCircular(circular, line, row, vehicle.price, financed);
  const ctx: OfferContext = {
    offer: circularToOffer(q),
    source: 'marca',
    circular: q
  };
  const sim = circularToSimulation(q);
  const isUva = line.rateType === 'uva';
  const summary: [string, string, boolean?][] = [['Precio del vehículo', formatARS(q.price)], ['Monto a financiar', formatARS(q.financed)], ['Anticipo (diferencia)', formatARS(q.down)], circular.managementFeePct === null ? ['Gastos de otorgamiento', 'A confirmar por la entidad'] : [`Gestión crediticia y gastos de otorgamiento (${circular.managementFeePct}%)`, formatARS(q.managementFee)], ['Total a abonar al inicio', formatARS(q.upfront), true], ['TNA', formatPct(row.tna)], ['CFT', 'No informado por la entidad'], ['Cuota cada $1.000', `$${row.installmentPer1000.toLocaleString('es-AR', {
    minimumFractionDigits: 2
  })}`], ['Total en cuotas', formatARS(q.totalInstallments)], ['Costo total estimado', formatARS(q.totalCost), true]];
  return <div className="grid gap-6 lg:grid-cols-[1fr_420px]" data-id="element-979" data-mp="2zxxw0chol54w">
      <div className="space-y-6" data-id="element-980" data-mp="2zzisi7nk2y5e">
        <div className="card flex items-center gap-4 p-4" data-id="element-981" data-mp="34wsmm0lw3xzk">
          <img src={vehicle.images[0]} alt="" className="h-16 w-24 rounded-lg object-cover" data-id="element-982" data-mp="34x13s4p1ppgk" />
          <div className="min-w-0 flex-1" data-id="element-983" data-mp="39uar9esvjr1r">
            <p className="truncate font-semibold" data-id="element-984" data-mp="3euozawuh70zs">{vehicle.model} {vehicle.version}</p>
            <p className="text-sm text-mist" data-id="element-985" data-mp="3esm29suwo7lk">Precio de contado {formatARS(vehicle.price)}</p>
          </div>
          <Link to={`/vehiculo/${vehicle.id}`} className="hidden text-sm text-neon hover:text-neon-hi sm:block" data-id="element-986" data-mp="u4pswrt2wzza">Ver detalle</Link>
        </div>

        <fieldset className="card p-5" data-id="element-987" data-mp="rque6fo6it4w">
          <legend className="sr-only" data-id="element-988" data-mp="rrw1pjmdt83i">Línea de financiación</legend>
          <p className="text-sm font-semibold" data-id="element-989" data-mp="nq24jlbeam83">Elegí la línea de financiación</p>
          <p className="mt-1 text-xs text-mist" data-id="element-990" data-mp="11pa5ain7i2ee">{circular.lender} · {circular.reference}</p>
          <div className="mt-4 grid gap-2 sm:grid-cols-2" data-id="element-991" data-mp="wow6lr7hw4t1">
            {lines.map(l => {
            const active = l.id === line.id;
            const minCoef = Math.min(...l.terms.map(t => t.tna));
            return <label key={l.id} className={`flex cursor-pointer flex-col rounded-xl border p-3.5 transition-colors duration-150 ${active ? 'border-neon bg-neon/10' : 'border-line hover:border-neon/50'}`} data-id="element-992" data-mp="16n3d1g45r4gs">
                  <input type="radio" name="circular-line" className="sr-only" checked={active} onChange={() => selectLine(l.id)} data-id="element-993" data-mp="1fhqddxuysqm6" />
                  <span className="flex items-start justify-between gap-2" data-id="element-994" data-mp="1pe37gwuf96hg">
                    <span className="font-semibold leading-tight" data-id="element-995" data-mp="1pc083m7dq102">{l.name}</span>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${l.rateType === 'uva' ? 'bg-warn/15 text-warn' : 'bg-neon/15 text-neon-hi'}`} data-id="element-996" data-mp="290ef4tpna9ps">{rateTypeLabel(l)}</span>
                  </span>
                  <span className="mt-0.5 text-xs text-mist" data-id="element-997" data-mp="240jqyxx97y8f">{l.detail}</span>
                  <span className="mt-2 text-sm" data-id="element-998" data-mp="23yp7zp5jmg6z">Hasta <strong data-id="element-999" data-mp="1pbvyj0ipg4mp">{formatARS(l.maxAmount)}</strong></span>
                  <span className="text-xs text-mist" data-id="element-1000" data-mp="1p9j77wr24hwz">
                    {l.terms[0].term} a {l.terms[l.terms.length - 1].term} meses · TNA desde {formatPct(minCoef)}
                  </span>
                </label>;
          })}
          </div>
          {line.audience === 'taxi-remis' && <p className="mt-3 rounded-lg bg-warn/10 px-3 py-2 text-xs text-warn" data-id="element-1001" data-mp="1l7o6atd1g6jq">Línea exclusiva para taxis y remises con habilitación vigente.</p>}
        </fieldset>

        <div className="card space-y-6 p-5" data-id="element-1002" data-mp="1bb31o94xkxgf">
          <fieldset data-id="element-1003" data-mp="s7rh68422ebf">
            <legend className="text-sm font-semibold" data-id="element-1004" data-mp="s8az3otph2pg">Plazo</legend>
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-1005" data-mp="ngszp57bza6l">
              {line.terms.map(t => <label key={t.term} className={`cursor-pointer rounded-xl border px-4 py-2 text-sm transition-colors duration-150 ${term === t.term ? 'border-neon bg-neon/10 text-white' : 'border-line text-mist hover:text-white'}`} data-id="element-1006" data-mp="ih6m2unlbrx9">
                  <input type="radio" name="circular-term" className="sr-only" checked={term === t.term} onChange={() => setTerm(t.term)} data-id="element-1007" data-mp="1b52gfspot7qv" />
                  <span className="block font-semibold" data-id="element-1008" data-mp="164xv9pdu3oub">{t.term} meses</span>
                  <span className={`block text-[11px] ${t.tna === 0 ? 'text-neon-hi' : ''}`} data-id="element-1009" data-mp="116cwzvi57f8i">TNA {formatPct(t.tna)}</span>
                </label>)}
            </div>
          </fieldset>

          <div data-id="element-1010" data-mp="3lzq2mzwwp9c8">
            <div className="flex items-baseline justify-between gap-3" data-id="element-1011" data-mp="3lw28byjn0iwg">
              <label htmlFor="circ-financed" className="text-sm font-semibold" data-id="element-1012" data-mp="3gyakpgq0ziy2">Monto a financiar</label>
              <span className="text-xs text-mist" data-id="element-1013" data-mp="1wim6dg01duaw">Máximo {formatARS(cap)}</span>
            </div>
            <input id="circ-financed" type="range" min={Math.min(STEP * 10, cap)} max={cap} step={STEP} value={q.financed} onChange={e => setFinanced(Number(e.target.value))} className="mt-3 w-full accent-[#1FA8FF]" data-id="element-1014" data-mp="21gnpg0xgptmd" />
            <p className="mt-2 font-display text-2xl font-bold" data-id="element-1015" data-mp="2k6lue85yr6ls">{formatARS(q.financed)}</p>
            <p className="text-xs text-mist" data-id="element-1016" data-mp="2k8gjzzs6jmw7">El resto del precio ({formatARS(q.down)}) se abona como anticipo.</p>
          </div>
        </div>

        <details className="card group p-5" data-id="element-1017" data-mp="2u3qkuk6udo8e">
          <summary className="cursor-pointer list-none text-sm font-semibold marker:hidden" data-id="element-1018" data-mp="2u2p6a5qmajlx">
            Ver todas las líneas de la circular para este modelo
          </summary>
          <div className="mt-4" data-id="element-1019" data-mp="zrafrmps8g54">
            <CircularTable circular={circular} lines={lines} selected={{
            lineId: line.id,
            term: row.term
          }} onSelect={(id, t) => selectLine(id, t)} data-id="element-1020" data-mp="31oaz6gkj50q5" />
          </div>
        </details>
      </div>

      <aside className="lg:sticky lg:top-24 lg:self-start" data-id="element-1021" data-mp="2wndf1yfylwm0">
        <div className="card overflow-hidden" aria-live="polite" data-id="element-1022" data-mp="3bcsx8q1fctq3">
          <div className="border-b border-line bg-panel p-5" data-id="element-1023" data-mp="36ehmmd3b2f6t">
            <p className="text-xs text-mist" data-id="element-1024" data-mp="36hdhttnrqprk">{isUva ? 'Cuota inicial estimada (en UVA)' : 'Cuota mensual fija'}</p>
            <p className="font-display text-4xl font-black tracking-tight text-neon" data-id="element-1025" data-mp="2dqdezm16bs1h">{formatARS(q.installment)}</p>
            <p className="mt-1 text-sm text-mist" data-id="element-1026" data-mp="2so5qm33lnwae">{row.term} cuotas · {line.name}</p>
            <div className="mt-3" data-id="element-1027" data-mp="2nojgi2j2iw5t"><OfferStatus offer={ctx.offer} source="marca" data-id="element-1028" data-mp="2no9xiywfxp9m" /></div>
          </div>
          <dl className="divide-y divide-line px-5 text-sm" data-id="element-1029" data-mp="7x58rsh9z6rn">
            {summary.map(([k, v, strong]) => <div key={k} className="flex justify-between gap-4 py-2.5" data-id="element-1030" data-mp="2yu19oegd8cl">
                <dt className="text-mist" data-id="element-1031" data-mp="ahthxmaj4ls9">{k}</dt>
                <dd className={`shrink-0 text-right ${v.startsWith('No informado') ? 'italic text-mist/70' : 'font-medium'} ${strong ? 'font-bold text-white' : ''}`} data-id="element-1032" data-mp="ah1y18qaocht">{v}</dd>
              </div>)}
          </dl>
          <div className="space-y-2 p-5 pt-3" data-id="element-1033" data-mp="1iy9s4nxd1mb">
            <button onClick={() => setAskOpen(true)} className="btn btn-primary w-full" data-id="element-1034" data-mp="5i7358f1yxhc">Solicitar asesoramiento con esta simulación</button>
            <Link to="/chequeo" className="btn btn-ghost w-full" data-id="element-1035" data-mp="t8up2xlsznmw">Chequeo crediticio</Link>
          </div>
          <div className="flex gap-2 border-t border-line p-5 text-[11px] leading-relaxed text-mist" data-id="element-1036" data-mp="y6mhdsqd1brm">
            <InfoIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden data-id="element-1037" data-mp="kfr669se2ikv" />
            <p data-id="element-1038" data-mp="og9zy9dudypp">
              Condiciones de la {circular.reference} de {circular.lender}, vigentes hasta el {formatDate(circular.validUntil)}. Cuota con IVA incluido, calculada con el coeficiente oficial cada $1.000.{' '}
              {isUva && 'En la línea UVA la cuota se expresa en UVA y se actualiza mensualmente según su cotización. '}
              {managementFeeText(circular)}{' '}
              {circular.notes.join(' ')}
            </p>
          </div>
        </div>
      </aside>

      <Modal open={askOpen} onClose={() => setAskOpen(false)} title="Solicitar asesoramiento" data-id="element-1039" data-mp="5og56nwnp7s9">
        <InquiryForm channel="simulador" vehicle={vehicle} simulation={{
        sim,
        ctx
      }} idPrefix="circ" data-id="element-1040" data-mp="anjam9gjtg0r" />
      </Modal>
    </div>;
}